/**
 * Checklist e atividades do projeto — o que já foi feito e o que falta.
 *
 * Duas coisas na mesma tela porque contam a mesma história: o checklist diz
 * **o que o projeto precisa ter** e a trilha de atividades diz **o que alguém
 * fez e quando**. No menu do Sistema de hoje elas são dois itens, mas quem
 * abre um abre o outro em seguida.
 *
 * **Cada marca grava sozinha, na hora.** É o `ChecklistAlterar` do legado: um
 * campo por vez, sem botão de salvar. Marcar oito e depois esquecer de salvar é
 * o jeito conhecido de perder trabalho, e aqui não há o que esquecer.
 */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams } from "react-router-dom"
import { api, noProjeto, obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { BarraDeEstado } from "@/kit/BarraDeEstado"

type ChecklistResponse = {
  projetoId: number
  diagramaDaInjecao: boolean
  conectoresDaInjecao: boolean
  caixaDeFusiveisDaBateria: boolean
  caixaDeFusiveisDoVaoDoMotor: boolean
  caixaDeFusiveisInterna: boolean
  oscilogramas: boolean
  diagramaDoArCondicionado: boolean
  conectoresDoArCondicionado: boolean
  diagramaDePartida: boolean
  diagramaDoImobilizador: boolean
}

type AtividadeResponse = {
  id: number
  projetoId: number
  data: string
  concluido: boolean
  concluidoData: string
  acao: string
  obs: string
  executores: string[]
}

/**
 * A ponte entre os dois nomes do mesmo campo.
 *
 * A resposta vem em camelCase, mas o PATCH quer o **nome da coluna** — é o que
 * o `ProjetoChecklist.Campos` aceita, e mandar outra coisa dá 400. Deixar os
 * dois lado a lado aqui evita descobrir isso por erro em produção.
 */
const ITENS: { chave: keyof ChecklistResponse; coluna: string; rotulo: string }[] = [
  // A lista e a ordem são as da produção (relatório de 28/09/2026). Partida e
  // imobilizador são colunas novas; "painel de instrumentos" é a antiga
  // "caixa de fusíveis interna", e "conector da injeção", os "conectores da
  // injeção". Oscilogramas e conectores do ar-condicionado saíram da tela, mas o
  // que já foi marcado neles continua gravado.
  { chave: "diagramaDaInjecao", coluna: "diagrama_da_injecao", rotulo: "Diagrama da injeção" },
  { chave: "diagramaDoArCondicionado", coluna: "diagrama_do_ar_condicionado", rotulo: "Diagrama do ar-condicionado" },
  { chave: "diagramaDePartida", coluna: "diagrama_de_partida", rotulo: "Diagrama de partida" },
  { chave: "diagramaDoImobilizador", coluna: "diagrama_do_imobilizador", rotulo: "Diagrama do imobilizador" },
  { chave: "caixaDeFusiveisDaBateria", coluna: "caixa_de_fusiveis_da_bateria", rotulo: "Caixa de fusíveis da bateria" },
  { chave: "caixaDeFusiveisDoVaoDoMotor", coluna: "caixa_de_fusiveis_do_vao_do_motor", rotulo: "Caixa de fusíveis do vão do motor" },
  { chave: "caixaDeFusiveisInterna", coluna: "caixa_de_fusiveis_interna", rotulo: "Caixa de fusíveis do painel de instrumentos" },
  { chave: "conectoresDaInjecao", coluna: "conectores_da_injecao", rotulo: "Conector da injeção" },
]

const dia = (d: string | null) => {
  if (!d || d.slice(0, 4) === "1900" || d.slice(0, 4) === "0001") return ""
  const [a, m, x] = d.slice(0, 10).split("-")
  return `${x}/${m}/${a}`
}

export function Checklist() {
  const { projetoId } = useParams()
  const id = Number(projetoId)
  const qc = useQueryClient()

  const checklist = useQuery({
    queryKey: ["checklist", id] as const,
    enabled: id > 0,
    queryFn: () => obter<ChecklistResponse>(`api/projetos/${id}/checklist`, {}),
  })

  const atividades = useQuery({
    queryKey: ["atividades", id] as const,
    enabled: id > 0,
    queryFn: () => obter<AtividadeResponse[]>(`api/projetos/${id}/atividades`, {}),
  })

  const marcar = useMutation({
    // `noProjeto(id)` porque o projeto vive no CAMINHO da URL, e a trava do
    // canário lê consulta e corpo, não caminho. Ela recusou a chamada em vez de
    // adivinhar — o comportamento certo, e o conserto é declarar o alvo, nunca
    // afrouxar a trava.
    mutationFn: async ({ coluna, valor }: { coluna: string; valor: boolean }) =>
      (await api.patch<ChecklistResponse>(
        `api/projetos/${id}/checklist`, { campo: coluna, valor }, noProjeto(id))).data,
    // A resposta já traz o checklist inteiro: escreve direto no cache em vez de
    // pedir de novo. Uma ida ao banco por marca, não duas.
    onSuccess: (novo) => qc.setQueryData(["checklist", id], novo),
  })

  const c = checklist.data
  const feitos = c ? ITENS.filter((i) => c[i.chave]).length : 0

  /**
   * A trilha inteira não vai para a tela.
   *
   * O projeto 976 tem **1.894 atividades**, e o endpoint devolve todas de uma
   * vez (não é paginado). Desenhar 1.894 cartões trava a rolagem e não ajuda
   * ninguém: quem abre isto quer ver o que andou agora. Mostra as 40 mais
   * recentes e deixa abrir o resto para quem procura coisa antiga.
   */
  const [tudo, setTudo] = useState(false)
  const trilha = atividades.data ?? []
  const visiveis = tudo ? trilha : trilha.slice(0, 40)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 18 }}>
        <div style={{ maxWidth: 760, display: "flex", flexDirection: "column", gap: 22 }}>
          <section>
            <h2 style={{ margin: "0 0 3px", fontSize: 14, fontWeight: 600 }}>Checklist</h2>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--txt2)" }}>
              O que este projeto precisa ter. Cada marca grava na hora — não há botão de salvar.
            </p>

            {checklist.isPending ? (
              <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
            ) : checklist.error ? (
              <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
                {checklist.error instanceof ErroDaApi
                  ? checklist.error.message : "Não foi possível ler o checklist."}
              </p>
            ) : (
              <div style={{
                border: "1px solid var(--line2)", borderRadius: 10, overflow: "hidden",
                background: "var(--surface)",
              }}>
                {ITENS.map((item, i) => {
                  const ligado = !!c?.[item.chave]
                  return (
                    <label
                      key={item.chave}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 13px",
                        borderTop: i === 0 ? undefined : "1px solid var(--line)",
                        cursor: marcar.isPending ? "progress" : "pointer",
                        background: ligado ? "var(--ok-soft)" : undefined,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={ligado}
                        disabled={marcar.isPending}
                        onChange={(e) => marcar.mutate({ coluna: item.coluna, valor: e.target.checked })}
                      />
                      <span style={{
                        fontSize: 12.5, fontWeight: ligado ? 600 : 500,
                        color: ligado ? "var(--ok-on-soft)" : "var(--txt)",
                      }}>
                        {item.rotulo}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}

            {marcar.error != null && (
              <div style={{
                marginTop: 10, padding: "9px 11px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
                background: marcar.error instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
                color: marcar.error instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
              }}>
                {marcar.error instanceof Error ? marcar.error.message : "Não foi possível gravar a marca."}
              </div>
            )}
          </section>

          <section>
            <h2 style={{ margin: "0 0 3px", fontSize: 14, fontWeight: 600 }}>Atividades</h2>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--txt2)" }}>
              A trilha do que foi feito neste projeto. É registro, não se edita por aqui.
            </p>

            {atividades.isPending ? (
              <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
            ) : trilha.length === 0 ? (
              <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>
                Nenhuma atividade registrada neste projeto.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visiveis.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 11, padding: "10px 13px",
                      border: "1px solid var(--line2)", borderRadius: 10, background: "var(--surface)",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        flex: "0 0 8px", width: 8, height: 8, marginTop: 5, borderRadius: "50%",
                        background: a.concluido ? "var(--ok)" : "var(--line2)",
                      }}
                    />
                    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{a.acao || "(sem descrição)"}</span>
                      {a.obs && (
                        <span style={{ fontSize: 11.5, color: "var(--txt2)", lineHeight: 1.45 }}>{a.obs}</span>
                      )}
                      {a.executores.length > 0 && (
                        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
                          {a.executores.join(", ")}
                        </span>
                      )}
                    </span>
                    <span style={{ flex: "0 0 auto", display: "flex", flexDirection: "column",
                                   alignItems: "flex-end", gap: 2 }}>
                      <span className="num" style={{ fontSize: 11.5, color: "var(--txt2)" }}>{dia(a.data)}</span>
                      {a.concluido && (
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ok-on-soft)" }}>
                          concluída {dia(a.concluidoData)}
                        </span>
                      )}
                    </span>
                  </div>
                ))}

                {!tudo && trilha.length > visiveis.length && (
                  <button
                    onClick={() => setTudo(true)}
                    style={{
                      alignSelf: "flex-start", height: 30, padding: "0 12px", borderRadius: 7,
                      border: "1px solid var(--line2)", background: "var(--surface)",
                      fontSize: 12, fontWeight: 600, color: "var(--txt2)",
                    }}
                  >
                    Mostrar as outras {(trilha.length - visiveis.length).toLocaleString("pt-BR")}
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <BarraDeEstado>
        {c && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{feitos}</b> de{" "}
            <b className="num" style={{ color: "var(--txt)" }}>{ITENS.length}</b> no checklist
          </span>
        )}
        {trilha.length > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{trilha.length.toLocaleString("pt-BR")}</b>{" "}
            {trilha.length === 1 ? "atividade" : "atividades"}
            {!tudo && trilha.length > visiveis.length && (
              <span style={{ color: "var(--txt3)" }}> · mostrando {visiveis.length}</span>
            )}
          </span>
        )}
      </BarraDeEstado>
    </div>
  )
}
